// Modules
import React, { useContext, useEffect, useMemo, useState } from "react";
//@ts-ignore
import { LocalizationContext } from "react-localize";
import BookingInfo from "Components/Pages/ApplyYourPoints/BookingInfo";
// Styles
import "./CancelConfirmation.sass";
import { ScreenType } from "../../../../Constants";
import { ReservationType } from "Interfaces/GetReservations";
import StayPreferences from "Components/Pages/Summary/StayPreferences";
import GuestProfile from "Components/Pages/Summary/GuestProfile";
import { ContractUsage, Preferences, Usage } from "Interfaces/AYPResultInterface";
import { AdditionalGuestProfile } from "Components/Pages/Summary/AdditionalGuestProfile";
import {
  GuestDetail,
  GuestEmail,
} from "Components/Pages/Summary/AdditionalGuestProfile/AdditionalGuestProfile";
import { Term } from "Interfaces/CreateWaitlist";
import { UsageDetails } from "Components/Pages/Summary/UsageDetails";
import { MvTermsConditions } from "Generic/MvTermsConditions";
import MvLink from "Generic/MvLink";
import MvIcon from "Generic/MvIcon";
import { ReactAppMode } from "Interfaces/ReactAppModes";
import { EXEC_MODE } from "cfg";
import moment from "moment";
import { MvPrimaryBtn } from "Generic/MvPrimaryBtn";
import { logAnalyticData } from "Utils/analytics";
import ToolTip from "Generic/ToolTip";
import ContractToolTip from "Interfaces/ContractToolTipInterface";
import ReservationService from "Services/reservation.service";
import { OwnerContract, OwnerShipDetails } from "Interfaces/OwnerInterface";
import MvContractToolTip from "Generic/ToolTip/MvContractToolTip";
import { Position } from "Generic/ToolTip/ToolTip";

type Props = {
  token: string;
  lang: string;
  resId: number;
  resType: ReservationType;
  waitListId?: number;
  setScreenType(screenType: ScreenType): void;
  screenType?: string;
  data: any;
  apiTimeout: string;
};

const CancelConfirmation = ({
  token,
  lang,
  resId,
  waitListId,
  resType,
  setScreenType,
  screenType,
  data,
  apiTimeout
  }: Props) => {
  const { localize } = useContext(LocalizationContext);
  const [usageTooltip, setUsageTooltip] = useState<Usage[]>([]);
  const [contractToolTips, setContractToolTips] = useState<ContractToolTip[]>(
    []
  );
  /** Datalayer for Adobe for Confirmation Page */

  useEffect(() => {
    if (data && data.cancelReservationResponse && data.reservationDetails) {
      logAnalyticData({ event: 'cancelPointsConfirmation', siteSection: 'Points Reservation', pageName:'Cancel Points Confirmation', website:'' }, {
        ownerActivity: 'Cancel Points',
        cancelStep: 'Step 3: Cancel Points Confirmation',
        cancellationResortName: data.reservationDetails.resortName,
        cancellationNumberOfNights: data.reservationDetails.numOfNights,
        cancellationCheckIn: data.reservationDetails.checkInDate,
        cancellationCheckOut: data.reservationDetails.checkOutDate,
        cancellationDate: data.cancelDate,
        cancellationNumber: data.reservationNumber,
        cancellationStatus: data.cancelReservationResponse.status,
        errorMessage: data.cancelReservationResponse.error,
        notificationType: data.cancelReservationResponse.error ? 'Alert' : ''
      })
    }
    const usageData = data.usageDetails ? data.usageDetails.usage || [] : [];
    setUsageTooltip(usageData);
  }, [data])

  const getSelectedPreferences = () => {
    const preferences = data.preferences || [];
    let selectedPreferences: any = { additionalRequests: [], roomRequests: {} };
    (preferences as Preferences).map((preference) => {
      preference.options.map((option) => {
        if (option.default) {
          selectedPreferences = {
            ...selectedPreferences,
            roomRequests: {
              ...selectedPreferences.roomRequests,
              [preference.code]: option.code,
            },
          };
        }
      });
    });
    return selectedPreferences;
  };

  const getPrimaryGuestInformation = () => {
    const detail = data.primaryGuest;
    return {
      ...detail,
    };
  };

  const getAdditionalGuests = (): GuestDetail[] => {
    return data.additionalGuests.filter((x: GuestDetail) => !!x.firstName);
  };

  const getAdditionalEmails = (): GuestEmail[] => {
    return data.additionalEmails
      .filter((x: string) => !!x)
      .map((x: string) => ({ email: x } as GuestEmail));
  };

  const termFilter = (term: Term): boolean => {
    // don't show email term if no emails are entered that are valid
    if (data.additionalEmails.length === 0) {
      return term.type !== "terms-additional-emails";
    }
    return true;
  };

  const getTerms = (): Term[] => {
    return data.terms.filter((term: Term) => termFilter(term));
  };

  const handleOwnershipSummaryClick = () => {
    if ([ReactAppMode.VCAP].includes(EXEC_MODE)) {
      window.open(`/checkpoints.jsp`, "_self");
    } else {
      window.open(`/timeshare/mvco/getProductSummary`, "_self");
    }
  };

  const handleReservationActivityClick = () => {
    if ([ReactAppMode.VCAP, ReactAppMode.VOA].includes(EXEC_MODE)) {
      setScreenType(ScreenType.RESERVATIONS);
      localStorage.setItem(
        `${ScreenType.RESERVATIONS}-tab`,
        ReservationType.CANCELED
      );
    } else {
      window.open(`/reservationActivity.jsp`, "_blank");
    }
  };

  const handleViewPointsBalance = () => {
    if ([ReactAppMode.VCAP].includes(EXEC_MODE)) {
      window.open(`/checkpoints.jsp`, "_blank");
    } else if ([ReactAppMode.VOA].includes(EXEC_MODE)) {
      window.open(`/timeshare/mvco/getProductSummary`, "_self");
    }
  };

  const print = () => {
    window.print();
  };

  const getUsageContractDetails = (): ContractUsage[] => {
    const usageDetails = data.usageDetails
    const contractDetails: ContractUsage[] = [];
    if (usageDetails && usageDetails.usage && usageDetails.usage.length > 0) {
        usageDetails.usage.forEach((item: any) => {
          contractDetails.push({ contractId: item.contractId, contractNumber: item.contractNumber, usageType: item.type, usageYear: item.year, appliedPoints: item.applied } as ContractUsage);
        });
    }
    if (usageDetails && usageDetails.borrowedUsage && usageDetails.borrowedUsage.length > 0) {
      usageDetails.borrowedUsage.forEach((item: any) => {
        contractDetails.push({ contractId: item.contractId, contractNumber: item.contractNumber, usageType: item.type, usageYear: item.year, appliedPoints: item.applied } as ContractUsage);
      });
    }
    if (usageDetails && usageDetails.refundedNormalUsageTypes && usageDetails.refundedNormalUsageTypes.length > 0) {
        usageDetails.refundedNormalUsageTypes.forEach((item: any) => {
          contractDetails.push({ contractId: item.contractId, contractNumber: item.contractNumber, refundChecked: true, usageType: item.type, usageYear: item.year, appliedPoints: item.applied, startDate: item.startDate, endDate: item.endDate, description: item.description } as unknown as ContractUsage);
        });
    }
    if (usageDetails && usageDetails.refundedHoldUsageTypes && usageDetails.refundedHoldUsageTypes.length > 0) {
      usageDetails.refundedHoldUsageTypes.forEach((item: any) => {
        contractDetails.push({ contractId: item.contractId, contractNumber: item.contractNumber, refundChecked: true, usageType: item.type, usageYear: item.year, appliedPoints: item.applied, startDate: item.startDate, endDate: item.endDate, description: item.description } as unknown as ContractUsage);
      });
    }

    if (usageDetails && usageDetails.refundedForefeitUsageTypes && usageDetails.refundedForefeitUsageTypes.length > 0) {
      usageDetails.refundedForefeitUsageTypes.forEach((item: any) => {
          contractDetails.push({ contractId: item.contractId, contractNumber: item.contractNumber, refundChecked: true, usageType: item.type, usageYear: item.year, appliedPoints: item.applied, startDate: item.startDate, endDate: item.endDate, description: item.description } as unknown as ContractUsage);
      });
    }

  return contractDetails;
}


const convertOCtoCTT = (oc: OwnerContract): ContractToolTip => {
  // the AYP table is still returning mock data? that will be returning real
  //  data this sprint by Rama - done by tomorrow perhaps
  // getting CORS error
  return {
    id: oc.id,
    contractId: oc.contractNumber,
    contractNumber:oc.contractNumber,
    contractType: oc.contractType,
    // hard code to points for now
    // the service is not providing this field
    // it is also not returning any weeks data
    typeName: "points",
    startDate: oc.contractStartDate,
    endDate: oc.contractEndDate,
    // this field is missing from the service
    // it is being looked into
    // so for now, just show the start date's year
    usageYear: oc.contractStartDate,
    points: oc.contractPoints,
    ownershipDetails: oc.ownershipDetails
      ? oc.ownershipDetails
      : ({} as OwnerShipDetails), //CE2RT1C-899 - 4. include ownership details when constructing contract tooltip
  };
};

const getContractToolTipJSX = (contractToolTip: ContractToolTip): any => {
  const contractId = contractToolTip.contractId;
  const key =
    contractId +
    contractToolTip.startDate +
    contractToolTip.endDate +
    (contractToolTip.points ? contractToolTip.points.toString() : "");
  const contractIdStyle = (
    <p
      key={key}
      className="contract-id mv-print-link"
      aria-label={contractId}
      style={{ textDecoration: 'underline' }}
    >
      {contractToolTip.contractNumber}
    </p>
  );

  return (
    <MvContractToolTip contract={contractToolTip} position={Position.RIGHT}>
      {contractIdStyle}
    </MvContractToolTip>
  );
};

const fetchContactTooltips = (contractList: any) => {
  // get the contracts' data
  const reservationService = new ReservationService();  
  return reservationService
    .getOwnerContracts(contractList)
    .then((ownerContracts: OwnerContract[]) => {
      // build the ContractToolTip[]
      let contractToolTipsData: ContractToolTip[] = contractToolTips || [];
      ownerContracts.map((contract) => {
        // convert OwnerContract to ContractToolTip
        let tempContractToolTip = convertOCtoCTT(contract);
        // get the tooltip jsx
        tempContractToolTip.toolTip = getContractToolTipJSX(
          tempContractToolTip
        );
        contractToolTipsData.push(tempContractToolTip);
      });
      return contractToolTipsData;
    });
};

const usageContractDetails = getUsageContractDetails();

useEffect(() => {
  async function fetchContactTooltipsData() {
    const payload = usageContractDetails.filter(x => !!x.contractNumber).map((x: any) => String((x as any).contractId));
    if (payload.length > 0) {
      var tooltipData = await fetchContactTooltips(payload);
      setContractToolTips(tooltipData);    
    }
  }
  if (contractToolTips.length == 0) {
    fetchContactTooltipsData();
    setContractToolTips([{ contractId: 'a' }as any])
  }
  }, [usageContractDetails]);

  return (
    <div className={`cancel-confirmation-container`}>
      <div className="page-header">
        <div className="page-header-links">
          <MvLink
            label="Ownership Summary"
            onClick={handleOwnershipSummaryClick}
          />
          <MvLink
            label="Reservation Activity"
            onClick={handleReservationActivityClick}
          />
        </div>
        <div>
          <h1>Cancelled Reservation</h1>
          <button className="print-container" onClick={print}>
            <MvIcon
              color="#5F5F5F"
              style={{
                height: "14px",
                width: "14px",
              }}
              icon={"print"}
            />
            <p>{localize("static.content.print-cta-label")}</p>
          </button>
        </div>
      </div>
      {data.hasResortCreditReservation && <div className="summary-alert">
        <MvIcon color="#990000" icon="exclamation" width="18px" height="18px" />
        <div>
            <p  dangerouslySetInnerHTML={{ __html: localize('static.content.cancellation-resort-credit-warning-description') }}></p>
        </div>
      </div>}
      {data.hasMarshaCancellationError && <div className="summary-alert">
        <MvIcon color="#990000" icon="exclamation" width="18px" height="18px" />
        <div>
            {EXEC_MODE == ReactAppMode.VCAP ? <p dangerouslySetInnerHTML={{ __html: localize('static.content.tsw-marsha-cancellation-error-vcap') }}></p> : <p dangerouslySetInnerHTML={{ __html: localize('static.content.tsw-marsha-cancellation-error-voa') }}></p>}
        </div>
      </div>}
      <div className="details-container">
        {data.cancelDate && (
          <div className="details-label">
            {localize("Cancel Date:")}
            {moment(data.cancelDate).format("D MMM YYYY")}
          </div>
        )}
        {data.reservationNumber && (
          <div className="details-label">
            {localize("static.content.reservations-reservation-num")}:
            {data.reservationNumber}
          </div>
        )}
      </div>
      <div className="cancel-header">
        <p className="cancel-title">{localize("static.content.cancellation-confirmed-header")}</p>
        <div className="cancel-content">
          <div className="cancel-content-text">
          {localize("static.content.cancellation-confirmed-description")}
            <p style={{ marginTop: "10px" }}>
              <b>{localize("static.content.reservations-reservation-num")}:</b> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{" "}
              {data.reservationNumber}
            </p>
          </div>
          <div className="cancel-content-button">
            <MvPrimaryBtn
              isDisabled={false}
              submissionInProgress={false}
              text={localize("static.content.view-points-balance-button-label")}
              onClick={handleViewPointsBalance}
            />
          </div>
        </div>
      </div>
      <BookingInfo
        reservationDetails={data.reservationDetails}
        pointsRequired={data.usageDetails.totalPointsReturned}
        onEditSearchClick={() => {}}
        readOnly={true}
        visitResortNewTab={true}
        setScreenType={() => {}}
        comingFrom='cancelReservation'
      />
      <div className="border-container">
      {data.hasMarshaCancellationError &&  <div className="ownership-summary">
          <p className="summary-header">{localize("static.content.ownership-usage-summary-header")}</p>
          <div className="summary-container">
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary.totalPoints >
                0 && (
                <div className="summary-items first">
                  <div className="summary-items-label">
                  {localize("static.content.originally-applied-label")}{" "}
                  </div>
                  <div className="summary-items-value">
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .totalPoints
                    }{" "}
                    Points
                  </div>
                </div>
              )}
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary
                .refundedPointsToOwner > 0 && (
                <div className="summary-items">
                  <div className="summary-items-label">
                    <div>{localize("static.content.refunded-to-you-label")}</div>
                    <ToolTip
                      useRefToPosition={false}
                      tip={[
                        {
                          label: "",
                          desc:
                            "Club Points are returned unrestricted when canceled 61 days or more prior to arrival.",
                        },
                      ]}
                      id="points-needed"
                    >
                      <span className="question-icon">{"?"}</span>
                    </ToolTip>
                  </div>
                  <div className="summary-items-value">
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .refundedPointsToOwner
                    }{" "}
                    {localize("static.content.points-label")}
                  </div>
                </div>
              )}
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary.holdPoints >
                0 && (
                <div className="summary-items">
                  <div className="summary-items-label">
                    <div>{localize("static.content.refunded-to-hold-label")}</div>
                    <ToolTip
                      useRefToPosition={false}
                      tip={[
                        {
                          label: "",
                          desc:
                            "Club Points in a holding account may be used to confirm another reservation within 60 days of arrival.",
                        },
                      ]}
                      id="points-needed"
                    >
                      <span className="question-icon">{"?"}</span>
                    </ToolTip>
                  </div>
                  <div className="summary-items-value">
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .holdPoints
                    }{" "}
                    Points
                  </div>
                </div>
              )}
            {data.cancelReservationPreviewOfUsages &&
              data.cancelReservationPreviewOfUsages.usageSummary &&
              data.cancelReservationPreviewOfUsages.usageSummary.forefeitPoints >
                0 && (
                <div className="summary-items">
                  <div className="summary-items-label">
                    <div>{localize("static.content.forfeited-label")}</div>
                    <ToolTip
                      useRefToPosition={false}
                      tip={[
                        {
                          label: "",
                          desc:
                            "Premium Points are forfeited on reservation canceled within 60 days of arrival.",
                        },
                      ]}
                      id="points-needed"
                    >
                      <span className="question-icon">{"?"}</span>
                    </ToolTip>
                  </div>
                  <div className="summary-items-value">
                    -
                    {
                      data.cancelReservationPreviewOfUsages.usageSummary
                        .forefeitPoints
                    }{" "}
                    Points
                  </div>
                </div>
              )}
          </div>
        </div>}
        <GuestProfile guest={getPrimaryGuestInformation()} readOnly={true} />
        <AdditionalGuestProfile
          readonly={true}
          additionalGuests={getAdditionalGuests()}
          emails={getAdditionalEmails()}
        />
        {!data.hasMarshaCancellationError && <UsageDetails
          contracts={usageContractDetails.map(x => ({ ...x, contractId: x.contractNumber }))}
          contractToolTips={contractToolTips}
          onContractsSort={() => {}}
          onEditClick={() => {}}
          readOnly={true}
          tablesInitOpen={true}
          resType={ReservationType.CANCELED}
          tablesExpandable={true}
          screenType={screenType}
          apiTimeout = {apiTimeout}
        />}
        <div className="summaryReviewTnC">
          <MvTermsConditions
            heading={localize("static.content.sr-tnc-heading")}
            subHeading={localize("static.content.sr-tnc-sub-heading")}
            instructions={getTerms()}
            onChange={() => {}}
            submissionFailed={false}
            readOnly={true}
          />
        </div>
      </div>
    </div>
  );
};

export { CancelConfirmation };
